<div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-xl">
  <div>
    <h1 class="font-headline-lg text-headline-lg text-text-main dark:text-on-surface mb-1">My Jobs</h1>
    @if (error()) {
      <p class="text-error font-body-sm text-body-sm">{{ error() }}</p>
    }
  </div>
</div>

@if (authService.isAdmin()) {
  <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl p-md flex items-center gap-3 mb-gutter shadow-sm">
    <label class="flex flex-col gap-1">
      <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Viewing</span>
      <select [value]="selectedTechnicianId() ?? ''" (change)="onTechnicianChange($any($event.target).value)"
              class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm h-[48px] text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2] min-w-[220px]">
        <option value="">Select technician...</option>
        @for (tech of technicians(); track tech.technicianId) {
          <option [value]="tech.technicianId">{{ tech.firstName }} {{ tech.lastName }}</option>
        }
      </select>
    </label>
    @if (matchedOwnTechnician()) {
      <span class="text-xs text-[#8DC63F] flex items-center gap-1 mt-5">
        <span class="material-symbols-outlined text-[16px]">check_circle</span>
        Matched to your signed-in account
      </span>
    }
  </div>
} @else if (matchedOwnTechnician()) {
  <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl p-md flex items-center gap-2 mb-gutter shadow-sm">
    <span class="material-symbols-outlined text-[#8DC63F] text-[20px]">check_circle</span>
    <p class="font-label-md text-text-main dark:text-on-surface">Your allocated jobs, {{ ownTechnicianName() }}</p>
  </div>
}

@if (!authService.isAdmin() && !matchedOwnTechnician()) {
  <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm shadow-sm">
    No technician profile is linked to your account yet. Ask an Admin or Operations to create one on the
    <a routerLink="/technicians" class="text-[#00B9F2] hover:underline">Technicians page</a> using this email.
  </div>
} @else if (authService.isAdmin() && !selectedTechnicianId()) {
  <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm shadow-sm">
    Pick a technician above to view their jobs.
  </div>
} @else {
  @if (loading()) {
    <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl shadow-sm">
      <p class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">Loading your jobs...</p>
    </div>
  } @else if (jobs().length === 0) {
    <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl shadow-sm">
      <p class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">No jobs assigned to you.</p>
    </div>
  } @else {
    <div class="flex flex-col gap-3">
      @for (job of jobs(); track job.jobId) {
        <div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 md:p-5 flex flex-col gap-3">
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="font-label-lg text-text-main dark:text-on-surface">{{ job.dealerName || 'N/A' }}</p>
              <span class="text-text-muted dark:text-on-surface-variant text-xs bg-surface dark:bg-surface-container-highest rounded-full px-2 py-0.5">Job #{{ job.jobId }}</span>
            </div>
            <span class="inline-flex items-center px-2 py-1 rounded-full font-label-sm border shrink-0"
                  [style.background-color]="statusColor(job.status) + '1A'"
                  [style.color]="statusColor(job.status)"
                  [style.border-color]="statusColor(job.status) + '33'">
              <span class="w-1.5 h-1.5 rounded-full mr-1.5" [style.background-color]="statusColor(job.status)"></span>
              {{ job.status }}
            </span>
          </div>

          <p class="font-body-sm text-text-main dark:text-on-surface flex items-center gap-1.5 flex-wrap">
            <span class="material-symbols-outlined text-[16px] text-text-muted dark:text-on-surface-variant">schedule</span>
            {{ job.scheduledDate ? (job.scheduledDate | date:'MMM d, y, h:mm a') : 'No scheduled time' }}
            @if (job.vehicleName) { <span class="text-text-muted dark:text-on-surface-variant">&middot;</span> {{ job.vehicleName }} }
            @if (job.jobTypes && job.jobTypes.length > 0) { <span class="text-text-muted dark:text-on-surface-variant">&middot;</span> {{ jobTypeNames(job) }} }
          </p>

          @if (job.jobTypes && job.jobTypes.length > 1) {
            <div class="flex flex-col gap-0.5 pl-[22px]">
              @for (jt of job.jobTypes; track jt.jobTypeId) {
                <p class="text-xs text-text-muted dark:text-on-surface-variant flex items-center justify-between gap-3 max-w-xs">
                  <span class="truncate">{{ jt.name }}</span>
                  <span class="font-mono shrink-0">{{ jt.durationMinutes ? formatDuration(jt.durationMinutes * 60) : 'N/A' }}</span>
                </p>
              }
            </div>
          }

          @if (job.durationMinutes || hasTimeTracking(job)) {
            <div class="flex items-center gap-4 flex-wrap bg-surface dark:bg-surface-container-highest rounded-lg px-3 py-2">
              @if (job.durationMinutes) {
                <span class="text-xs font-mono flex items-center gap-1.5">
                  <span class="text-text-muted dark:text-on-surface-variant">Est.</span>
                  <span class="text-text-main dark:text-on-surface">{{ formatDuration(job.durationMinutes * 60) }}</span>
                </span>
              }
              @if (hasTimeTracking(job)) {
                <span class="text-xs font-mono flex items-center gap-1.5">
                  <span class="text-text-muted dark:text-on-surface-variant">{{ isCompleted(job) ? 'Actual' : 'Elapsed' }}</span>
                  <span class="font-medium" [style.color]="actualTimeColor(job)">
                    {{ formatDuration(elapsedSeconds(job)) }}
                  </span>
                </span>
              }
              @if (isCompleted(job) && job.modifiedAt) {
                <span class="text-xs flex items-center gap-1.5">
                  <span class="material-symbols-outlined text-[16px] text-[#8DC63F]">task_alt</span>
                  <span class="text-text-muted dark:text-on-surface-variant">Completed {{ job.modifiedAt | date:'MMM d, h:mm a' }}</span>
                </span>
              }
            </div>
          }

          @if (job.notes) {
            <div class="flex flex-col gap-1">
              <p class="text-text-muted dark:text-on-surface-variant text-xs border-l-2 border-border-subtle dark:border-outline-variant pl-2"
                 [class.line-clamp-2]="isLongNotes(job.notes) && !isNotesExpanded(job.jobId)">{{ job.notes }}</p>
              @if (isLongNotes(job.notes)) {
                <button type="button" (click)="toggleNotes(job.jobId)" class="text-xs text-[#00B9F2] hover:opacity-80 self-start pl-2">
                  {{ isNotesExpanded(job.jobId) ? 'Show less' : 'Show more' }}
                </button>
              }
            </div>
          }

          @if (canAdvance(job) || job.status === 'Arrived' || isRunning(job) || isPaused(job) || canCancel(job)) {
            <div class="flex items-center gap-2 flex-wrap justify-end pt-2 border-t border-border-subtle dark:border-outline-variant">
              @if (job.status === 'EnRoute' && getDirectionsUrl(job); as directionsUrl) {
                <a [href]="directionsUrl" target="_blank" rel="noopener"
                   class="px-3 py-1.5 text-xs rounded-lg border border-[#F29727] text-[#F29727] hover:bg-[#F29727]/10 transition-colors font-label-sm flex items-center gap-1.5">
                  <span class="material-symbols-outlined text-[16px]">directions</span>
                  Get Directions
                </a>
              }
              @if (canAdvance(job)) {
                <button (click)="advanceJob(job)"
                        class="px-3 py-1.5 text-xs rounded-lg border border-[#00B9F2] text-[#00B9F2] hover:bg-[#00B9F2]/10 transition-colors font-label-sm">
                  Mark {{ nextStatus(job) }}
                </button>
              }
              @if (job.status === 'Arrived') {
                <button (click)="openStartModal(job)"
                        class="px-3 py-1.5 text-xs rounded-lg bg-[#00B9F2] text-white hover:opacity-90 transition-opacity font-label-sm">
                  Start Job
                </button>
              }
              @if (isPaused(job)) {
                <span class="px-2 py-1 text-[11px] rounded-full bg-[#F29727]/10 text-[#F29727] font-label-sm flex items-center gap-1">
                  <span class="material-symbols-outlined text-[14px]">pause_circle</span>
                  Paused by Operations
                </span>
                <button (click)="resumeJob(job)"
                        class="px-3 py-1.5 text-xs rounded-lg bg-[#F29727] text-white hover:opacity-90 transition-opacity font-label-sm">
                  Resume Job
                </button>
              }
              @if (isRunning(job) || isPaused(job)) {
                <button (click)="openCompleteModal(job)"
                        class="px-3 py-1.5 text-xs rounded-lg bg-[#8DC63F] text-white hover:opacity-90 transition-opacity font-label-sm">
                  Complete Job
                </button>
              }
              @if (canCancel(job)) {
                <button (click)="cancelJob(job)" title="Cancel job"
                        class="p-2 text-text-muted dark:text-on-surface-variant hover:text-[#ef4444] rounded hover:bg-surface-variant dark:hover:bg-surface-container-highest transition-colors">
                  <span class="material-symbols-outlined text-[20px]">block</span>
                </button>
              }
            </div>
          }
        </div>
      }
    </div>
  }
}

@if (startModalOpen()) {
  <div class="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" (click)="closeStartModal()">
    <div class="bg-background dark:bg-surface-container-low rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
      <div class="p-lg border-b border-border-subtle dark:border-outline-variant flex items-center justify-between">
        <h2 class="font-headline-sm text-headline-sm text-text-main dark:text-on-surface">Start Job</h2>
        <button (click)="closeStartModal()" class="p-1 rounded-full hover:bg-surface-variant dark:hover:bg-surface-variant text-text-muted dark:text-on-surface-variant">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="p-lg flex flex-col gap-4">
        <label class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Before photos <span class="text-error">*</span></span>
          <input type="file" accept="image/*" capture="environment" multiple (change)="onStartPhotosSelected($event)"
                 class="text-body-sm text-text-main dark:text-on-surface">
          @if (startPhotos().length === 0) {
            <span class="text-xs text-text-muted dark:text-on-surface-variant">At least one before photo is required to start this job.</span>
          }
        </label>

        @if (startPhotos().length > 0) {
          <div class="flex flex-wrap gap-2">
            @for (photo of startPhotos(); track $index) {
              <div class="relative w-20 h-20 rounded-lg overflow-hidden border border-border-subtle dark:border-outline-variant">
                <img [src]="photo" class="w-full h-full object-cover" alt="Before photo" />
                <button type="button" (click)="removeStartPhoto($index)" title="Remove photo"
                        class="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center">
                  <span class="material-symbols-outlined text-[14px]">close</span>
                </button>
              </div>
            }
          </div>
        }

        <label class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Notes</span>
          <textarea [value]="startNotes()" (input)="startNotes.set($any($event.target).value)" rows="3"
                    class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]"></textarea>
        </label>
      </div>

      <div class="p-lg border-t border-border-subtle dark:border-outline-variant flex items-center justify-end gap-3">
        <button type="button" (click)="closeStartModal()"
                class="h-[44px] px-6 border border-border-subtle dark:border-outline-variant rounded-lg text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors font-label-md">
          Cancel
        </button>
        <button type="button" (click)="confirmStartJob()" [disabled]="starting() || startPhotos().length === 0"
                class="h-[44px] px-6 bg-[#00B9F2] text-white rounded-lg hover:opacity-90 transition-opacity font-label-md disabled:opacity-50">
          {{ starting() ? 'Starting...' : 'Start Job' }}
        </button>
      </div>
    </div>
  </div>
}

@if (completeModalOpen()) {
  <div class="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" (click)="closeCompleteModal()">
    <div class="bg-background dark:bg-surface-container-low rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
      <div class="p-lg border-b border-border-subtle dark:border-outline-variant flex items-center justify-between">
        <h2 class="font-headline-sm text-headline-sm text-text-main dark:text-on-surface">Complete Job</h2>
        <button (click)="closeCompleteModal()" class="p-1 rounded-full hover:bg-surface-variant dark:hover:bg-surface-variant text-text-muted dark:text-on-surface-variant">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <div class="p-lg flex flex-col gap-4">
        <label class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">After photos <span class="text-error">*</span></span>
          <input type="file" accept="image/*" capture="environment" multiple (change)="onCompletePhotosSelected($event)"
                 class="text-body-sm text-text-main dark:text-on-surface">
          @if (completePhotos().length === 0) {
            <span class="text-xs text-text-muted dark:text-on-surface-variant">At least one after photo is required to complete this job.</span>
          }
        </label>

        @if (completePhotos().length > 0) {
          <div class="flex flex-wrap gap-2">
            @for (photo of completePhotos(); track $index) {
              <div class="relative w-20 h-20 rounded-lg overflow-hidden border border-border-subtle dark:border-outline-variant">
                <img [src]="photo" class="w-full h-full object-cover" alt="After photo" />
                <button type="button" (click)="removeCompletePhoto($index)" title="Remove photo"
                        class="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center">
                  <span class="material-symbols-outlined text-[14px]">close</span>
                </button>
              </div>
            }
          </div>
        }

        <label class="flex flex-col gap-1">
          <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Notes</span>
          <textarea [value]="completeNotes()" (input)="completeNotes.set($any($event.target).value)" rows="3"
                    class="bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]"></textarea>
        </label>

        @if (completeNeedsAdvisorEmail()) {
          <label class="flex flex-col gap-1">
            <span class="font-label-sm text-text-muted dark:text-on-surface-variant">Service Advisor Email <span class="text-xs">(so they can get the feedback survey)</span></span>
            <input [value]="completeServiceAdvisorEmail()" (input)="completeServiceAdvisorEmail.set($any($event.target).value)" type="email" placeholder="advisor@dealer.com"
                   class="min-w-0 w-full bg-surface dark:bg-surface-container-highest border border-border-subtle dark:border-outline-variant rounded-lg px-3 py-2 text-body-sm text-text-main dark:text-white focus:outline-none focus:border-[#00B9F2] focus:ring-1 focus:ring-[#00B9F2]">
          </label>
        }
      </div>

      <div class="p-lg border-t border-border-subtle dark:border-outline-variant flex items-center justify-end gap-3">
        <button type="button" (click)="closeCompleteModal()"
                class="h-[44px] px-6 border border-border-subtle dark:border-outline-variant rounded-lg text-text-main dark:text-on-surface hover:bg-surface-variant dark:hover:bg-surface-variant transition-colors font-label-md">
          Cancel
        </button>
        <button type="button" (click)="confirmCompleteJob()" [disabled]="completing() || completePhotos().length === 0"
                class="h-[44px] px-6 bg-[#8DC63F] text-white rounded-lg hover:opacity-90 transition-opacity font-label-md disabled:opacity-50">
          {{ completing() ? 'Completing...' : 'Complete Job' }}
        </button>
      </div>
    </div>
  </div>
}
